import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { readMemberFromStorage } from '../utils/memberSession';
import memberApi from '../services/memberApi';
import { DROPSHIPPING_MAX_QUANTITY } from '../constants/cartLimits';
import {
  buildPresentationVariants,
  detectPresentationUnitLabel,
  extractBaseName,
  extractProductClaim,
  getPresentationPriceRange,
  resolveProductPageCopy,
} from '../utils/productPresentations';
import { ProductWellnessCrossLink } from './EcosystemCrossLink';
import './ProductDetailPage.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Mapeo categoría API → breadcrumb
const CATEGORY_LABELS = {
  'coleccion-1': 'Ropa hombre',
  ropa: 'Ropa',
  'ropa-hombre': 'Ropa hombre',
  'ropa-mujer': 'Ropa mujer',
  'productos-naturales': 'Productos naturales',
  'instrumentos-musicales': 'Instrumentos musicales',
  'joyeria-artesanias': 'Joyería y Artesanías',
  'bienestar': 'Bienestar',
  'tecnologia': 'Tecnología',
  'jardineria': 'Jardinería',
  camisetas: 'Ropa hombre'
};

const ProductDetailPage = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [expandedSection, setExpandedSection] = useState('info');

  // Formulario de reseña
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 0, comment: '', hover: 0 });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewStatus, setReviewStatus] = useState(null); // 'success' | 'error'

  // Wishlist
  const [inWishlist, setInWishlist] = useState(false);
  const [relatedScroll, setRelatedScroll] = useState(0);
  const reviewsRef = useRef(null);
  
  const { addToCart, openCart } = useCart();

  // Carga inicial + re-carga al navegar entre productos via SPA
  useEffect(() => {
    const handleNavigation = () => {
      const path = window.location.pathname;
      const match = path.match(/\/producto\/(\d+)/);
      if (match) {
        window.scrollTo(0, 0);
        setProduct(null);
        setRelatedProducts([]);
        setReviews([]);
        setSelectedImage(0);
        setQuantity(1);
        setExpandedSection('info');
        fetchProduct(match[1]);
      } else {
        setError('Producto no encontrado');
        setLoading(false);
      }
    };

    handleNavigation();
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar productos relacionados y reseñas cuando hay producto
  useEffect(() => {
    if (!product?.id) return;
    
    const loadRelated = async () => {
      try {
        const res = await axios.get(`${API_URL}/products`, { params: { limit: 50 } });
        const all = res.data.data?.products || res.data.products || [];
        const filtered = all.filter(p => p.id !== product.id).slice(0, 18);
        setRelatedProducts(filtered);
      } catch (e) {
        console.error('Error cargando relacionados:', e);
      }
    };
    
    const loadReviews = async () => {
      try {
        const res = await axios.get(`${API_URL}/reviews/product/${product.id}`);
        setReviews(res.data.data?.reviews || res.data.reviews || []);
      } catch (e) {
        setReviews([]);
      }
    };
    
    loadRelated();
    loadReviews();

    // Wishlist: verificar si este producto ya está guardado
    try {
      const wl = JSON.parse(localStorage.getItem('javiland_wishlist') || '[]');
      setInWishlist(wl.includes(product.id));
    } catch { setInWishlist(false); }

    // Pre-llenar nombre si el miembro está logueado
    const member = readMemberFromStorage();
    if (member) {
      const firstName = (member.first_name || member.name || '').trim().split(' ')[0];
      setReviewForm(prev => ({ ...prev, name: firstName || member.email || '' }));
    }
  }, [product?.id]);

  const fetchProduct = async (productId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/products/${productId}`);
      
      // El backend devuelve: { success: true, data: { product: {...} } }
      const productData = response.data.data?.product || response.data.product;
      
      if (!productData) {
        setError('Producto no encontrado');
        setLoading(false);
        return;
      }

      let allProducts = [];
      try {
        const allProductsResponse = await axios.get(`${API_URL}/products`, { params: { limit: 200 } });
        allProducts = allProductsResponse.data.data?.products || allProductsResponse.data.products || [];
      } catch (err) {
        console.error('Error obteniendo catálogo:', err);
      }

      // Formatear producto
      const mediaSlots = productData.media || {};
      const galleryFromMedia = ['back', 'left', 'right', 'diagonal', 'detail']
        .map((k) => String(mediaSlots[k] || '').trim())
        .filter(Boolean);
      const productImagesRaw =
        productData.images && productData.images.length > 0
          ? productData.images
          : productData.image
            ? [productData.image]
            : [];
      const productImages = [...new Set([...productImagesRaw.map((u) => String(u).trim()), ...galleryFromMedia])].filter(
        Boolean
      );
      const productImage =
        productImages[0] || '/images/placeholders/placeholder-product.png';
      
      const isDropshipping = productData.fulfillment === 'dropshipping';
      const catalogStatus = productData.status || 'active';
      const isUpcoming = catalogStatus === 'upcoming';
      const catSlug = String(productData.category || '').toLowerCase();
      const isRopa =
        catSlug.includes('ropa') || String(productData.name || '').toLowerCase().includes('camiseta');
      const sellerName = isRopa
        ? (productData.brand && String(productData.brand).trim()) || 'Javiland'
        : (productData.brand && String(productData.brand).trim()) || 'Costal Campesino';

      const sizeOrderMap = { XS: 0, S: 1, M: 2, L: 3, XL: 4, XXL: 5, Único: 99 };
      const rawSizeVariants =
        (Array.isArray(productData.variants) && productData.variants.length > 0
          ? productData.variants
          : null) ||
        (Array.isArray(productData.media?.sizeVariants) && productData.media.sizeVariants.length > 0
          ? productData.media.sizeVariants
          : null) ||
        [];

      let formattedProduct = {
        id: productData.id,
        name: productData.name,
        brand: productData.brand || (isRopa ? 'Javiland' : ''),
        sellerName,
        description: productData.description || 'Producto de calidad premium de Javiland.',
        price: parseFloat(productData.price),
        status: catalogStatus,
        isUpcoming,
        originalPrice: (() => {
          const raw = parseFloat(
            productData.originalPrice ?? productData.original_price ??
            productData.compare_at_price ?? productData.compareAtPrice ?? 0
          );
          return Number.isFinite(raw) && raw > parseFloat(productData.price) ? raw : null;
        })(),
        image: productImage,
        images: productImages.length
          ? productImages
          : ['/images/placeholders/placeholder-product.png'],
        features: productData.features || [],
        inStock: !isUpcoming && (productData.stock > 0 || isDropshipping),
        isDropshipping,
        fulfillment: productData.fulfillment || 'javiland',
        stock: productData.stock,
        category: productData.category,
        usesSizeVariants: !!productData.usesSizeVariants,
        variants: [{
          size: 'Único',
          color: 'Único',
          stock: isDropshipping ? 99 : productData.stock,
          price: parseFloat(productData.price)
        }]
      };

      if (productData.usesSizeVariants && rawSizeVariants.length > 0) {
        const variantes = rawSizeVariants
          .map((v) => ({
            size: String(v.size || 'Único').toUpperCase(),
            color: v.color || 'Único',
            stock: Number(v.stock) || 0,
            price: parseFloat(v.price != null ? v.price : productData.price)
          }))
          .sort((a, b) => (sizeOrderMap[a.size] ?? 99) - (sizeOrderMap[b.size] ?? 99));
        formattedProduct.variants = variantes;
        formattedProduct.stock = variantes.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
        formattedProduct.inStock = !isUpcoming && variantes.some((v) => (Number(v.stock) || 0) > 0);
      } else if (
        !productData.usesSizeVariants &&
        productData.name.toLowerCase().includes('camiseta')
      ) {
        // Legacy: varias filas por talla (IDs sueltos). No usar si el producto ya tiene variants.
        const camisetas = allProducts.filter(p => p.name.toLowerCase().includes('camiseta'));
          
          if (camisetas.length > 0) {
            const sizeToIdMap = {};
            const variantes = camisetas.map(c => {
              const match = c.name.match(/-?\s*([XSML]+)$/i);
              const talla = match ? match[1].trim().toUpperCase() : 'Único';
              sizeToIdMap[talla] = c.id;
              return {
                size: talla,
                color: 'Único',
                stock: c.stock,
                price: parseFloat(c.price)
              };
            });
            
            variantes.sort((a, b) => {
              const orderA = sizeOrderMap[a.size] !== undefined ? sizeOrderMap[a.size] : 99;
              const orderB = sizeOrderMap[b.size] !== undefined ? sizeOrderMap[b.size] : 99;
              return orderA - orderB;
            });
            
            formattedProduct.variants = variantes;
            formattedProduct.stock = variantes.reduce((sum, v) => sum + v.stock, 0);
            formattedProduct.inStock = !isUpcoming && variantes.some(v => v.stock > 0);
            formattedProduct.sizeToIdMap = sizeToIdMap;
            formattedProduct.name = 'Camiseta Javiland';
            formattedProduct.brand = 'Javiland';
            formattedProduct.sellerName = 'Javiland';
          }
      }

      const presentationVariants = buildPresentationVariants(productData, allProducts);
      if (presentationVariants) {
        formattedProduct.presentationVariants = presentationVariants;
        formattedProduct.displayName = extractBaseName(productData.name);
        formattedProduct.claim = extractProductClaim(productData.name);
        formattedProduct.presentationUnitLabel = detectPresentationUnitLabel(
          presentationVariants.map((v) => v.label)
        );
      } else {
        formattedProduct.displayName = extractBaseName(productData.name);
        formattedProduct.claim = extractProductClaim(productData.name);
      }

      const pageCopy = resolveProductPageCopy(
        formattedProduct.presentationVariants,
        formattedProduct.description
      );
      formattedProduct.descriptionSummary = pageCopy.summary;
      formattedProduct.descriptionFull = pageCopy.detail;

      setProduct(formattedProduct);
      
      // Auto-seleccionar primera talla con stock (o la primera si todas en 0)
      if (formattedProduct.variants.length > 0) {
        const firstInStock =
          formattedProduct.variants.find((v) => (Number(v.stock) || 0) > 0) ||
          formattedProduct.variants[0];
        setSelectedSize(firstInStock.size);
        setSelectedColor(firstInStock.color || 'Único');
      }
      
      setError(null);
    } catch (err) {
      console.error('Error cargando producto:', err);
      setError('Error al cargar el producto. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Formatear precio — muestra $X.XXX COP
  const formatPrice = (price) => {
    const num = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
    return `$${num}`;
  };

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Wishlist toggle
  const handleWishlist = () => {
    const member = readMemberFromStorage();
    if (!member) {
      window.history.pushState({}, '', '/comunidad');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    try {
      const wl = JSON.parse(localStorage.getItem('javiland_wishlist') || '[]');
      let updated;
      if (inWishlist) {
        updated = wl.filter(id => id !== product.id);
      } else {
        updated = [...wl, product.id];
      }
      localStorage.setItem('javiland_wishlist', JSON.stringify(updated));
      setInWishlist(!inWishlist);
    } catch { /* ignore */ }
  };

  // Enviar reseña (requiere sesión de miembro)
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (reviewForm.rating < 1) return;
    setReviewSubmitting(true);
    setReviewStatus(null);
    try {
      await memberApi.post('/reviews', {
        productId: product.id,
        rating: reviewForm.rating,
        comment: reviewForm.comment.trim(),
      });
      setReviewStatus('success');
      setReviewForm(prev => ({ ...prev, rating: 0, comment: '', hover: 0 }));
    } catch (err) {
      if (err?.response?.status === 409) {
        setReviewStatus('duplicate');
      } else {
        setReviewStatus('error');
      }
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handlePresentationSelect = (variantId) => {
    const id = parseInt(variantId, 10);
    if (!product?.presentationVariants || id === product.id) return;

    const variant = product.presentationVariants.find((v) => v.id === id);
    if (!variant) return;

    setProduct((prev) => ({
      ...prev,
      id: variant.id,
      name: variant.name,
      displayName: prev.displayName || extractBaseName(variant.name),
      price: variant.price,
      originalPrice: variant.originalPrice,
      stock: variant.stock,
      inStock: variant.inStock,
      isDropshipping: variant.isDropshipping,
      fulfillment: variant.fulfillment,
      image: variant.image,
      images: variant.images,
      brand: variant.brand || prev.brand,
      variants: [{
        size: 'Único',
        color: 'Único',
        stock: variant.isDropshipping ? 99 : variant.stock,
        price: variant.price,
      }],
    }));
    setSelectedImage(0);
    setQuantity(1);
    window.history.replaceState({}, '', `/producto/${variant.id}`);
  };

  // Manejar agregar al carrito
  const handleAddToCart = () => {
    if (!product) return;
    
    const finalSize = selectedSize || product.variants[0]?.size || 'Único';
    const finalColor = selectedColor || product.variants[0]?.color || 'Único';
    
    // Verificar stock disponible (dropshipping siempre pasa)
    const variant = product.variants.find(
      v => v.size === finalSize && v.color === finalColor
    );

    if (!product.isDropshipping && (!variant || (variant.stock || 0) < quantity)) {
      alert('Stock insuficiente para esta variante');
      return;
    }

    addToCart(product, finalSize, finalColor, quantity);
    openCart();
  };

  // Verificar si la variante está en stock (dropshipping siempre disponible)
  const isVariantInStock = (size, color) => {
    if (product?.isDropshipping) return true;
    const variant = product?.variants.find(v => v.size === size && v.color === color);
    return variant && (variant.stock || 0) > 0;
  };

  // Navegar de vuelta
  const goBack = () => {
    window.history.pushState({}, '', '/tienda');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando producto...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-page">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error || 'Producto no encontrado'}</p>
          <button onClick={goBack} className="btn-back">Volver a la tienda</button>
        </div>
      </div>
    );
  }

  // Ordenar tallas de XS a XL (de izquierda a derecha)
  const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Único'];
  const availableSizes = [...new Set(product.variants.map(v => v.size))]
    .sort((a, b) => {
      const indexA = sizeOrder.indexOf(a.toUpperCase());
      const indexB = sizeOrder.indexOf(b.toUpperCase());
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  const availableColors = [...new Set(product.variants.map(v => v.color))];
  const totalStock = product.variants.reduce((total, variant) => total + (variant.stock || 0), 0);
  const isAvailable = !product.isUpcoming && product.inStock && (totalStock > 0 || product.isDropshipping);

  const categoryLabel = CATEGORY_LABELS[product.category] || product.category || 'Productos';
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1) : null;
  const totalPages = Math.ceil(relatedProducts.length / 6);
  const visibleRelated = relatedProducts.slice(relatedScroll * 6, relatedScroll * 6 + 6);
  const hasMultiplePresentations = product.presentationVariants?.length > 1;
  const presentationRange = getPresentationPriceRange(product.presentationVariants);
  const titleName = product.displayName || product.name;
  const productClaim = product.claim || extractProductClaim(product.name);
  const showPlusOneKgCta =
    isAvailable &&
    (product.presentationUnitLabel === 'Gramos' ||
      String(product.category || '').toLowerCase().includes('natural') ||
      String(product.category || '').toLowerCase().includes('seco') ||
      String(product.category || '').toLowerCase().includes('snack'));

  const goToProduct = (id) => {
    window.history.pushState({}, '', `/producto/${id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="product-detail-page">
      <main className="product-detail-main">
        <div className="container">
          {/* Breadcrumbs */}
          <nav className="product-breadcrumbs" aria-label="Navegación">
            <a href="/" onClick={(e) => { e.preventDefault(); window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}>Home</a>
            <span className="breadcrumb-sep">›</span>
            <a href="/tienda" onClick={(e) => { e.preventDefault(); goBack(); }}>Tienda</a>
            <span className="breadcrumb-sep">›</span>
            <span>{categoryLabel}</span>
            <span className="breadcrumb-sep">›</span>
            <span className="breadcrumb-current">{titleName}</span>
          </nav>

          <div className="product-detail-content">
            {/* Galería de imágenes */}
            <div className="product-gallery-section">
              <div className="main-image-container">
                <button
                  type="button"
                  className="gallery-nav gallery-prev"
                  onClick={() => setSelectedImage((prev) => (prev === 0 ? (product.images?.length || 1) - 1 : prev - 1))}
                  aria-label="Imagen anterior"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <img
                  src={product.images[selectedImage] || product.image || '/images/placeholders/placeholder-product.png'}
                  alt={product.name}
                  className="main-product-image"
                  onError={(e) => {
                    e.target.src = '/images/placeholders/placeholder-product.png';
                  }}
                />
                <button
                  type="button"
                  className="gallery-nav gallery-next"
                  onClick={() => setSelectedImage((prev) => (prev >= (product.images?.length || 1) - 1 ? 0 : prev + 1))}
                  aria-label="Siguiente imagen"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              
              {/* Thumbnails */}
              {product.images && product.images.length > 1 && (
                <div className="image-thumbnails">
                  {product.images.map((image, index) => (
                    <img
                      key={index}
                      src={image || '/images/placeholders/placeholder-product.png'}
                      alt={`${product.name} - Vista ${index + 1}`}
                      className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                      onError={(e) => {
                        e.target.src = '/images/placeholders/placeholder-product.png';
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Información del producto */}
            <div className="product-info-section">
              {product.brand && (
                <span className="product-brand">{product.brand}</span>
              )}
              <h1 className="product-title">{titleName}</h1>
              {productClaim && (
                <p className="product-claim">{productClaim}</p>
              )}
              <p className="product-seller">Vendido por {product.sellerName || product.brand || 'Javiland'}</p>

              {hasMultiplePresentations && presentationRange && (
                <div className="product-price-range-header">
                  <span className="price-main">
                    {presentationRange.min === presentationRange.max
                      ? formatPrice(presentationRange.min)
                      : `${formatPrice(presentationRange.min)} – ${formatPrice(presentationRange.max)}`}
                  </span>
                  <span className="price-currency-tag">COP</span>
                  <span className="price-range-note">Según presentación</span>
                </div>
              )}

              {/* Valoración — clic lleva a reseñas */}
              <button type="button" className="product-rating product-rating-link" onClick={scrollToReviews} title="Ver opiniones">
                <span className="rating-stars">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className={`fas fa-star ${avgRating && s <= parseFloat(avgRating) ? 'filled' : ''}`}></i>
                  ))}
                </span>
                <span className="rating-text">
                  {avgRating ? `${avgRating}/5` : 'Sin valoraciones'} · {reviews.length} opiniones
                </span>
              </button>

              {/* Resumen corto — mismo copy para todas las presentaciones */}
              {product.descriptionSummary && (
                <div className="product-desc-block product-desc-block--summary">
                  <h3 className="product-desc-heading">Sobre este producto</h3>
                  <p className="product-desc-summary">{product.descriptionSummary}</p>
                </div>
              )}

              {/* Presentación — antes de cantidad para ver CTA en primera pantalla */}
              {isAvailable && hasMultiplePresentations && (
                <div className="product-variants presentation-select-group">
                  <label className="variant-label" htmlFor="presentation-select">
                    {product.presentationUnitLabel}
                  </label>
                  <select
                    id="presentation-select"
                    className="presentation-select"
                    value={product.id}
                    onChange={(e) => handlePresentationSelect(e.target.value)}
                  >
                    {product.presentationVariants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label} — {formatPrice(v.price)} COP
                      </option>
                    ))}
                  </select>
                  {showPlusOneKgCta && (
                    <a
                      className="product-plus-one-kg"
                      href={`mailto:+1kg@javiland.com?subject=${encodeURIComponent(`Mayoreo +1 kg · ${titleName}`)}&body=${encodeURIComponent(`Hola, quiero cotizar +1 kg (o mayoreo) de: ${titleName} (ref #${product.id}).\n\nNombre:\nCiudad:\nCantidad aproximada:\n`)}`}
                    >
                      ¿Necesitas +1 kg o mayoreo? Escríbenos (sin pago en línea)
                    </a>
                  )}
                </div>
              )}
              {showPlusOneKgCta && !hasMultiplePresentations && (
                <a
                  className="product-plus-one-kg"
                  href={`mailto:+1kg@javiland.com?subject=${encodeURIComponent(`Mayoreo +1 kg · ${titleName}`)}&body=${encodeURIComponent(`Hola, quiero cotizar +1 kg (o mayoreo) de: ${titleName} (ref #${product.id}).\n\nNombre:\nCiudad:\nCantidad aproximada:\n`)}`}
                >
                  ¿Necesitas +1 kg o mayoreo? Escríbenos (sin pago en línea)
                </a>
              )}

              {/* Variantes — tallas/colores (mostrar también si usesSizeVariants con ≥1 talla) */}
              {isAvailable &&
                (product.usesSizeVariants
                  ? availableSizes.length >= 1
                  : availableSizes.length > 1 || availableColors.length > 1) && (
                <div className="product-variants">
                  {/* Selección de talla */}
                  {(product.usesSizeVariants ? availableSizes.length >= 1 : availableSizes.length > 1) && (
                    <div className="variant-group">
                      <label className="variant-label">Talla:</label>
                      <div className="variant-options">
                        {availableSizes.map(size => (
                          <button
                            key={size}
                            className={`variant-option ${selectedSize === size ? 'selected' : ''} ${!availableColors.some(color => isVariantInStock(size, color)) ? 'out-of-stock' : ''}`}
                            onClick={() => setSelectedSize(size)}
                            disabled={!availableColors.some(color => isVariantInStock(size, color))}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selección de color */}
                  {selectedSize && availableColors.length > 1 && (
                    <div className="variant-group">
                      <label className="variant-label">Color:</label>
                      <div className="variant-options">
                        {availableColors.map(color => (
                          <button
                            key={color}
                            className={`variant-option ${selectedColor === color ? 'selected' : ''} ${!isVariantInStock(selectedSize, color) ? 'out-of-stock' : ''}`}
                            onClick={() => setSelectedColor(color)}
                            disabled={!isVariantInStock(selectedSize, color)}
                          >
                            {color}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cantidad */}
              <div className="quantity-selector">
                <label>Cantidad:</label>
                <div className="quantity-controls">
                  <button
                    className="quantity-btn"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span className="quantity-display">{quantity}</span>
                  <button
                    className="quantity-btn"
                    onClick={() => setQuantity(quantity + 1)}
                    disabled={product.isDropshipping ? quantity >= DROPSHIPPING_MAX_QUANTITY : quantity >= (product.variants.find(v => v.size === selectedSize && v.color === selectedColor)?.stock || 0)}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Precio inline — debajo de cantidad, precio de la presentación seleccionada */}
              <div className="product-price-inline">
                <span className="price-main">{formatPrice(product.price)}</span>
                <span className="price-currency-tag">COP</span>
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="price-original-inline">{formatPrice(product.originalPrice)} COP</span>
                )}
              </div>

              {/* Sección unificada: disponibilidad + envío */}
              <div className="delivery-unified">
                <h4 className="delivery-unified-title">
                  <i className="fas fa-truck"></i> Domicilio
                </h4>
                <div className="delivery-unified-row">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Envío nacional <strong>$12.000 COP</strong> · toda Colombia</span>
                </div>
                <div className="delivery-unified-row">
                  {product.isDropshipping
                    ? (
                      <>
                        <i className="fas fa-clock" style={{ color: '#7a9e7e' }}></i>
                        <span>
                          Disponible bajo pedido · entrega estimada <strong>5–6 días hábiles</strong> · envío a toda Colombia
                        </span>
                      </>
                    )
                    : (
                      <>
                        <i className="fas fa-box-open"></i>
                        <span>En stock en bodega Chía · {product.variants.find(v => v.size === selectedSize && v.color === selectedColor)?.stock || 0} unidades disponibles</span>
                      </>
                    )
                  }
                </div>
              </div>

              {/* Botón agregar al carrito */}
              <div className="add-to-cart-row">
                {product.isUpcoming ? (
                  <div className="product-upcoming-banner">
                    <i className="fas fa-clock" />
                    <span><strong>Próximo lanzamiento</strong> — disponible muy pronto. Activa alertas desde Acceso.</span>
                  </div>
                ) : null}
                <button
                  className="add-to-cart-btn-large"
                  onClick={handleAddToCart}
                  disabled={!isAvailable || (selectedSize && selectedColor && !isVariantInStock(selectedSize, selectedColor))}
                >
                  <i className="fas fa-shopping-cart"></i>
                  {product.isUpcoming ? 'Próximamente' : 'Agregar al carrito'}
                </button>
                <button
                  type="button"
                  className={`btn-wishlist${inWishlist ? ' active' : ''}`}
                  aria-label={inWishlist ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                  onClick={handleWishlist}
                  title={inWishlist ? 'Quitar de favoritos' : 'Guardar en favoritos'}
                >
                  <i className={inWishlist ? 'fas fa-heart' : 'far fa-heart'}></i>
                </button>
              </div>

            </div>
          </div>

          {/* Secciones desplegables — orden: info, especificaciones, devoluciones */}
          <div className="product-accordions">

            {/* 1. Información adicional — abierto por defecto */}
            <div className={`accordion-item ${expandedSection === 'info' ? 'open' : ''}`}>
              <button type="button" className="accordion-header" onClick={() => setExpandedSection(expandedSection === 'info' ? null : 'info')}>
                <span><i className="fas fa-leaf accordion-icon"></i> ¿Por qué elegir este producto?</span>
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="accordion-content">
                {(product.descriptionFull || product.description) && (
                  (product.descriptionFull || product.description)
                    .split('\n')
                    .filter((l) => l.trim() && l.trim() !== '---DETALLE---')
                    .map((line, i) => (
                      <p key={i}>{line.startsWith('•') ? line : line}</p>
                    ))
                )}
                {product.features && product.features.length > 0 && (
                  <ul>
                    {product.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                )}
                <p className="accordion-brand-note">
                  En Javiland seleccionamos cada producto con intención — nada llega a nuestra tienda sin pasar por nuestro filtro de calidad y propósito.
                </p>
              </div>
            </div>

            {/* 2. Especificaciones */}
            <div className={`accordion-item ${expandedSection === 'especificaciones' ? 'open' : ''}`}>
              <button type="button" className="accordion-header" onClick={() => setExpandedSection(expandedSection === 'especificaciones' ? null : 'especificaciones')}>
                <span><i className="fas fa-info-circle accordion-icon"></i> Especificaciones</span>
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="accordion-content">
                <dl className="specs-list">
                  <dt>Categoría</dt>
                  <dd>{categoryLabel}</dd>
                  <dt>Marca</dt>
                  <dd>{product.brand || product.sellerName || 'Javiland'}</dd>
                  <dt>Disponibilidad</dt>
                  <dd>
                    {product.isDropshipping
                      ? 'Disponible bajo pedido (entrega estimada 5–6 días hábiles)'
                      : 'En stock en bodega Chía'}
                  </dd>
                  <dt>Referencia</dt>
                  <dd>#{product.id}</dd>
                </dl>
              </div>
            </div>

            {/* 3. Devoluciones — al final */}
            <div className={`accordion-item ${expandedSection === 'devoluciones' ? 'open' : ''}`}>
              <button type="button" className="accordion-header" onClick={() => setExpandedSection(expandedSection === 'devoluciones' ? null : 'devoluciones')}>
                <span><i className="fas fa-shield-alt accordion-icon"></i> Compra con confianza</span>
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="accordion-content">
                <p>Tu tranquilidad es nuestra prioridad. Si el producto no es lo que esperabas, tienes <strong>30 días</strong> desde la recepción para contactarnos y buscar la mejor solución.</p>
                <p>En caso de defecto o error nuestro, el cambio o devolución es completamente sin costo para ti. Nuestro equipo responde rápido — somos personas reales que cuidan cada pedido.</p>
                <p>
                  <a
                    href="/refund-policy"
                    className="accordion-link"
                    onClick={(e) => {
                      e.preventDefault();
                      window.history.pushState({}, '', '/refund-policy');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                  >
                    Conoce nuestra política completa →
                  </a>
                </p>
              </div>
            </div>

          </div>

          <ProductWellnessCrossLink category={product.category} />

          {/* Productos relacionados */}
          {relatedProducts.length > 0 && (
            <section className="related-products-section">
              <h2 className="related-title">Quienes vibran como tú también descubren</h2>
              <div className="related-carousel">
                <button type="button" className="carousel-nav carousel-prev" onClick={() => setRelatedScroll((p) => Math.max(0, p - 1))} disabled={relatedScroll === 0} aria-label="Anterior">
                  <i className="fas fa-chevron-left"></i>
                </button>
                <div className="related-grid">
                  {visibleRelated.map((p) => (
                    <article key={p.id} className="related-card" onClick={() => goToProduct(p.id)}>
                      <div className="related-card-image">
                        <img src={p.image || p.images?.[0] || '/images/placeholders/placeholder-product.png'} alt={p.name} />
                      </div>
                      <span className="related-card-brand">{p.brand || 'Javiland'}</span>
                      <h3 className="related-card-name">{p.name}</h3>
                      <span className="related-card-price">{formatPrice(p.price)}</span>
                    </article>
                  ))}
                </div>
                <button type="button" className="carousel-nav carousel-next" onClick={() => setRelatedScroll((p) => Math.min(totalPages - 1, p + 1))} disabled={relatedScroll >= totalPages - 1} aria-label="Siguiente">
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              {totalPages > 1 && (
                <div className="carousel-dots">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button key={i} type="button" className={`dot ${i === relatedScroll ? 'active' : ''}`} onClick={() => setRelatedScroll(i)} aria-label={`Página ${i + 1}`} />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Opiniones del producto */}
          <section className="product-reviews-section" ref={reviewsRef}>
            <h2 className="reviews-title">Opiniones de este producto</h2>
            <div className="reviews-summary">
              <div className="reviews-rating-big">
                <span className="rating-num">{avgRating || '—'}</span>
                <span className="rating-max">/5</span>
                <div className="rating-stars-big">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className={`fas fa-star ${avgRating && s <= parseFloat(avgRating) ? 'filled' : ''}`}></i>
                  ))}
                </div>
                <span className="reviews-count">{reviews.length} comentarios</span>
              </div>
              {reviews.length > 0 && (
                <div className="reviews-distribution">
                  {[5,4,3,2,1].map((stars) => {
                    const count = reviews.filter(r => (r.rating || 0) === stars).length;
                    return (
                      <div key={stars} className="dist-row">
                        <span>{stars}*</span>
                        <div className="dist-bar"><div className="dist-fill" style={{ width: `${(count / reviews.length) * 100}%` }} /></div>
                        <span>{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="reviews-list">
              {reviews.length === 0 ? (
                <p className="no-reviews">Aún no hay opiniones. ¡Sé el primero en comentar!</p>
              ) : (
                reviews.map((r) => (
                  <div key={r.id || r.created_at} className="review-card">
                    <div className="review-header">
                      <span className="review-author">{r.customer_name || 'Anónimo'}</span>
                      <span className="review-stars">
                        {[1,2,3,4,5].map((s) => <i key={s} className={`fas fa-star ${(r.rating || 0) >= s ? 'filled' : ''}`}></i>)}
                      </span>
                      <span className="review-date">{r.created_at ? new Date(r.created_at).toLocaleDateString('es-CO') : ''}</span>
                    </div>
                    <p className="review-comment">{r.comment || ''}</p>
                  </div>
                ))
              )}
            </div>

            {/* Formulario para dejar reseña — requiere sesión */}
            <div className="review-form-wrapper">
              <div className="review-form-header">
                <i className="fas fa-pen-to-square review-form-icon"></i>
                <h3 className="review-form-title">Comparte tu experiencia</h3>
              </div>

              {(() => {
                const member = readMemberFromStorage();

                // Sin sesión → CTA para registrarse
                if (!member) {
                  return (
                    <div className="review-login-cta">
                      <div className="review-login-cta-icon">
                        <i className="fas fa-user-circle"></i>
                      </div>
                      <p className="review-login-cta-text">
                        Para dejar una opinión necesitas una cuenta en Javiland.
                        Es gratis y solo toma un minuto.
                      </p>
                      <button
                        type="button"
                        className="review-login-cta-btn"
                        onClick={() => {
                          window.history.pushState({}, '', '/comunidad');
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                      >
                        <i className="fas fa-arrow-right"></i> Crear cuenta o ingresar
                      </button>
                    </div>
                  );
                }

                // Éxito
                if (reviewStatus === 'success') {
                  return (
                    <div className="review-success">
                      <i className="fas fa-check-circle"></i>
                      <div>
                        <strong>¡Gracias, {member.first_name || member.name?.split(' ')[0] || 'amigo/a'}!</strong>
                        <p>Tu opinión fue recibida. La revisaremos y la publicaremos pronto.</p>
                      </div>
                    </div>
                  );
                }

                // Duplicado
                if (reviewStatus === 'duplicate') {
                  return (
                    <div className="review-success" style={{borderColor:'#e8b400', background:'#fffbea'}}>
                      <i className="fas fa-info-circle" style={{color:'#c99b00'}}></i>
                      <div>
                        <strong>Ya tienes una opinión registrada para este producto.</strong>
                        <p>Solo se permite una reseña por persona.</p>
                      </div>
                    </div>
                  );
                }

                // Formulario
                const memberName = (member.first_name || member.name || '').split(' ')[0];
                return (
                  <form className="review-form" onSubmit={handleReviewSubmit}>
                    {/* Identificación del miembro */}
                    <div className="review-member-badge">
                      <i className="fas fa-user-check"></i>
                      <span>Opinando como <strong>{memberName || member.email}</strong></span>
                    </div>

                    {/* Estrellas interactivas */}
                    <div className="review-stars-input">
                      <span className="review-stars-label">¿Cuántas estrellas le das?</span>
                      <div className="review-stars-selector">
                        {[1,2,3,4,5].map((s) => (
                          <button
                            key={s}
                            type="button"
                            className={`star-btn${(reviewForm.hover || reviewForm.rating) >= s ? ' active' : ''}`}
                            onMouseEnter={() => setReviewForm(p => ({ ...p, hover: s }))}
                            onMouseLeave={() => setReviewForm(p => ({ ...p, hover: 0 }))}
                            onClick={() => setReviewForm(p => ({ ...p, rating: s }))}
                            aria-label={`${s} estrella${s > 1 ? 's' : ''}`}
                          >
                            <i className="fas fa-star"></i>
                          </button>
                        ))}
                        {reviewForm.rating > 0 && (
                          <span className="review-stars-hint">
                            {['','Malo','Regular','Bueno','Muy bueno','Excelente'][reviewForm.rating]}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Comentario */}
                    <div className="review-field">
                      <label htmlFor="review-comment">Tu opinión <span className="review-optional">(opcional)</span></label>
                      <textarea
                        id="review-comment"
                        rows={3}
                        placeholder="Cuéntanos tu experiencia con este producto…"
                        value={reviewForm.comment}
                        onChange={e => setReviewForm(p => ({ ...p, comment: e.target.value }))}
                        maxLength={800}
                      />
                    </div>

                    {reviewStatus === 'error' && (
                      <p className="review-error-msg"><i className="fas fa-exclamation-circle"></i> Ocurrió un error. Intenta de nuevo.</p>
                    )}

                    <button
                      type="submit"
                      className="review-submit-btn"
                      disabled={reviewSubmitting || reviewForm.rating < 1}
                    >
                      {reviewSubmitting
                        ? <><i className="fas fa-spinner fa-spin"></i> Enviando…</>
                        : <><i className="fas fa-paper-plane"></i> Publicar opinión</>
                      }
                    </button>
                  </form>
                );
              })()}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ProductDetailPage;

